import { useState } from "react";
import { Button, CircularProgress, Dialog, Grid, Box, Typography, TextField } from "@mui/material";
import DynamicGrid from "../components/DynamicTable";
import { fetchGuardarPresupuesto } from "../services/presupuestoService";
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { requestResultModel } from "../models/RequestReturnModel";
import LocalSelector from "./components/LocalSelector";
import { useVentaPresupuesto } from "../hooks/useVentaPresupuesto";

export const gridColumnDefinitions = {
  lineaVentas: {
    'PRODUCTO.PRODUCTO': { Header: 'Producto', Editable: false },
    'PRODUCTO.CONTADO': { Header: '$', Editable: false, IsNumber: true },
    'CANTIDAD': { Header: 'Cantidad', Editable: true, IsNumber: true },
    'DELETE': { Header: 'Eliminar', Editable: false, CustomCell: true },
  },
  productos: {
    'PRODUCTO': { Header: 'Producto', Editable: false },
    'CODIGO': { Header: 'Codigo', Editable: false },
    'CONTADO': { Header: '$ Unitario', Editable: false, IsNumber: true },
  },
  clientes: {
    'CLIENTE': { Header: 'Cliente', Editable: false },
  },
};

const Presupuesto = () => {
  const {
    idLocal,
    selectDialogOpen,
    clienteSeleccionado,
    lineaVenta,
    data,
    seleccionandoCliente,
    loadingProductos,
    userLoggedIn,
    handleSelectClienteDialog,
    handleSelectProductoDialog,
    eventHandler,
    handleDeleteRow,
    handleLocalChange,
    resetForm,
    showMessage,
    setSelectDialogOpen,
  } = useVentaPresupuesto();

  const [plazo, setPlazo] = useState<number>(30);

  const guardarPresupuesto = () => {
    if (!userLoggedIn || !clienteSeleccionado || !idLocal || isNaN(Number(idLocal))) {
      showMessage("Error al guardar el presupuesto", 'error');
      return;
    }
    if (lineaVenta.length === 0) {
      showMessage("No hay productos en el presupuesto", 'error');
      return;
    }
    if (!plazo || plazo <= 0) {
      showMessage("Debe ingresar un plazo valido", 'error');
      return;
    }

    fetchGuardarPresupuesto(userLoggedIn!, clienteSeleccionado!.ID, Number(idLocal), lineaVenta, plazo)
      .then((res: requestResultModel) => {
        showMessage(res.message, res.success ? 'success' : 'error');
        if (res.success) {
          resetForm();
          setPlazo(30);
        }
      })
      .catch(err => {
        showMessage("Error al guardar el presupuesto", 'error');
        console.log(err);
      });
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Presupuesto
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="h6" gutterBottom>
            Seleccione un Local para el presupuesto:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={2}>
          <LocalSelector onLocalChange={handleLocalChange} />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectClienteDialog}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={<PersonIcon />}
          >
            Seleccionar Cliente
          </Button>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Button
            onClick={handleSelectProductoDialog}
            variant="contained"
            color="secondary"
            fullWidth
            disabled={loadingProductos || !idLocal}
            sx={{ textTransform: "none", fontWeight: "bold", py: 1.5 }}
            startIcon={loadingProductos ? <CircularProgress color="inherit" size={18} /> : <ShoppingCartIcon />}
          >
            {loadingProductos ? "Cargando..." : "Seleccionar Producto"}
          </Button>
        </Grid>
      </Grid>

      {/* Dialog */}
      <Dialog 
        open={selectDialogOpen} 
        fullWidth 
        keepMounted
        onClose={() => setSelectDialogOpen(false)}
      >
        <Typography variant="h4" sx={{ p: 2 }}>
          {seleccionandoCliente ? "Seleccionar Cliente" : "Seleccionar Producto"}
        </Typography>
        <DynamicGrid
          data={data}
          customHeaderMap={
            seleccionandoCliente
              ? gridColumnDefinitions.clientes
              : gridColumnDefinitions.productos
          }
          pageSize={5}
          removeIfNotCustom={true}
          rowClickEventHandler={eventHandler}
        />
      </Dialog>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Presupuesto para: {clienteSeleccionado?.CLIENTE || "Seleccione un cliente"}
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Plazo (dias)"
              type="number"
              value={plazo}
              onChange={(e) => setPlazo(Number(e.target.value))}
              fullWidth
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
        </Grid>
      </Box>

      <DynamicGrid
        data={lineaVenta.map((linea, index) => ({
          ...linea,
          DELETE: "",
        }))}
        customHeaderMap={gridColumnDefinitions.lineaVentas}
        pageSize={5}
        removeIfNotCustom={true}
        customRenderers={{
          DELETE: (row, rowIndex) => (
            <Button
              onClick={() => handleDeleteRow(rowIndex)}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
            >
              Eliminar
            </Button>
          ),
        }}
      />
      <Box sx={{ mt: 3, textAlign: "right" }}>
        <Button
          onClick={guardarPresupuesto}
          variant="contained"
          color="success"
          sx={{ textTransform: "none", fontWeight: "bold", px: 4, py: 1.5 }}
          startIcon={<ReceiptIcon />}
        >
          Guardar Presupuesto
        </Button>
      </Box>
    </Box>
  );
}

export default Presupuesto;


